import { useCallback } from 'react'
import { useIntegrations } from '../views/Integration/useIntegrations'
import { selectAccountId } from '../store/auth/authSlice'
import { useAppSelector } from '../store/hooks'
import { useGetMeQuery } from '../store/user/user'
import { useGetAccountQuery } from '../store/accountUserSettings/accountUserSettings'
import { useZocksQuota } from '../components/ScopedBlock/hooks'
import { IntegrationRequirementResult, INTEGRATIONS_REQUIRING_USER_CONNECTION, isZoomStrictJoinRequired } from './integrationRequirement'
import { IntegrationServiceId } from './integrations'

/**
 * Returns a checker that evaluates whether the current user satisfies the requirements to use a given integration provider.
 *
 * Requirement rules:
 * 1. Account must allow the integration (enterprise config)
 * 2. If the provider requires a user-level connection (Zoom):
 *    - User must have a 'live' integration
 *    - Exception: for Zoom, when the `integration.zoomStrictJoinEnabled` feature quota is enabled
 *      AND strict-join (companion mode) is not required by enterprise enforcement or the user's own
 *      preference, the bot can fall back to the service-user ZAK token, so no live integration is
 *      required. When the quota is off, the live-integration requirement applies as before.
 */
export const useIntegrationRequirement = () => {
  const accountId = useAppSelector(selectAccountId)
  const { isIntegrationAllowed } = useIntegrations()
  const { data: currentUser } = useGetMeQuery({ accountId })
  const { data: accountResponse } = useGetAccountQuery({ accountId })
  const { hasQuota } = useZocksQuota()
  const isZoomStrictJoinFeatureEnabled = hasQuota('integration.zoomStrictJoinEnabled.value')

  return useCallback(
    (serviceId: IntegrationServiceId): IntegrationRequirementResult => {
      const accountAllowed = isIntegrationAllowed(serviceId)
      const hasLiveIntegration = currentUser?.integrations?.some((i) => i.serviceId === serviceId && i.state === 'live') ?? false
      const requiresUserConnection = INTEGRATIONS_REQUIRING_USER_CONNECTION.has(serviceId)
      const zoomDoesNotRequireUserConnection =
        serviceId === IntegrationServiceId.ZOOM && isZoomStrictJoinFeatureEnabled && !isZoomStrictJoinRequired(currentUser, accountResponse?.data)
      const satisfied = accountAllowed ? hasLiveIntegration || !requiresUserConnection || zoomDoesNotRequireUserConnection : false
      return { satisfied, accountAllowed }
    },
    [isIntegrationAllowed, currentUser, accountResponse, isZoomStrictJoinFeatureEnabled],
  )
}
